class HeliosCollectionTabs extends HTMLElement {
  constructor() {
    super();

    this.onClick = this.onClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onResize = this.onResize.bind(this);
  }

  connectedCallback() {
    this.tablist = this.querySelector('[role="tablist"]');
    this.buttons = [...this.querySelectorAll('[role="tab"]')];
    this.panels = [...this.querySelectorAll('[role="tabpanel"]')];
    this.content = this.querySelector('.featured-collection-tabs__content');

    if (!this.buttons.length || !this.panels.length) return;

    this.buttons.forEach((button, index) => {
      button.tabIndex = button.classList.contains('is-active') ? 0 : -1;

      button.addEventListener('click', this.onClick);
      button.addEventListener('keydown', this.onKeydown);
    });

    this.resizeObserver = new ResizeObserver(this.onResize);

    const activePanel = this.getActivePanel();

    if (activePanel) {
      this.resizeObserver.observe(activePanel);
    }

    requestAnimationFrame(() => {
      this.updateHeight();
    });

    this.updateButtonUrlFromActivePanel();
  }

  disconnectedCallback() {
    this.buttons.forEach((button) => {
      button.removeEventListener('click', this.onClick);
      button.removeEventListener('keydown', this.onKeydown);
    });

    this.resizeObserver?.disconnect();
  }

  onResize() {
    this.updateHeight(false);
  }

  getActiveButton() {
    return this.buttons.find((button) =>
      button.classList.contains('is-active')
    );
  }

  getActivePanel() {
    return this.panels.find((panel) =>
      panel.classList.contains('is-active')
    );
  }

  onClick(event) {
    this.activate(event.currentTarget, true);
  }

  onKeydown(event) {
    const currentIndex = this.buttons.indexOf(event.currentTarget);
    let nextIndex = currentIndex;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        nextIndex = (currentIndex + 1) % this.buttons.length;
        break;

      case 'ArrowLeft':
      case 'ArrowUp':
        nextIndex =
          (currentIndex - 1 + this.buttons.length) %
          this.buttons.length;
        break;

      case 'Home':
        nextIndex = 0;
        break;

      case 'End':
        nextIndex = this.buttons.length - 1;
        break;

      case 'Enter':
      case ' ':
      case 'Spacebar':
        event.preventDefault();
        this.activate(event.currentTarget, true);
        return;

      default:
        return;
    }

    event.preventDefault();
    this.buttons[nextIndex].focus();
    this.activate(this.buttons[nextIndex], false);
  }

  activate(button, moveFocus) {
    const id = button.dataset.tab;
    const previousPanel = this.getActivePanel();

    this.buttons.forEach((btn) => {
      const active = btn === button;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-selected', active);
      btn.tabIndex = active ? 0 : -1;
    });

    this.panels.forEach((panel) => {
      const active = panel.dataset.panel === id;
      panel.classList.toggle('is-active', active);
      panel.setAttribute('aria-hidden', !active);
    });

    if (moveFocus) {
      button.focus({ preventScroll: true });
    }

    if (previousPanel) {
      this.resizeObserver.unobserve(previousPanel);
    }

    const activePanel = this.getActivePanel();

    if (activePanel) {
      this.resizeObserver.observe(activePanel);
    }

    this.updateHeight(true);

    this.updateButtonUrlFromActivePanel();
  }

  updateHeight(animate = true) {
    const panel = this.getActivePanel();

    if (!panel || !this.content) return;

    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    if (!animate || prefersReducedMotion) {
      this.content.style.height = `${panel.offsetHeight}px`;
      return;
    }

    const startHeight = this.content.offsetHeight;
    const endHeight = panel.offsetHeight;

    this.content.style.height = `${startHeight}px`;
    requestAnimationFrame(() => {
      this.content.style.height = `${endHeight}px`;
    });
  }

  updateButtonUrlFromActivePanel() {

    const activePanel = this.getActivePanel();
    if (!activePanel) return;
    
    const collectionUrl = activePanel.getAttribute('data-collection-url');
    if (!collectionUrl) return;
  }
}

if (!customElements.get('helios-collection-tabs')) {
  customElements.define(
    'helios-collection-tabs',
    HeliosCollectionTabs
  );
}